// COP — the LOCAL PDP integration (CONTRACT:I4-POLICY-DECISION.1.0).
//
// Wraps the vendored @tak-tdf/abac-pdp evaluator for the COP: loads the policy
// bundle once, tracks the current operator's entitlements + a registry of the
// WHOLE picture, and decides show-vs-hide per object. The KAS (I5) still releases
// keys on open; this only filters visibility (it never grants access).
//
// Two-state (AOA §3): permit -> show (+ 🔒 TDF PROTECTED badge if the payload is
// encrypted); deny -> the object is removed from the engine (existence not shown).
//
// ── CLS_ID equivalence-class recompute engine (docs/AOA-CLASSID-RECOMPUTE.md) ──
// The per-publish recompute used to scan the WHOLE registry running a fresh
// decideVisibility per object (2×O(N)×decide — the 44.8ms@5k cliff). A visibility
// decision is a pure function of (marking, subject), and thousands of objects share
// only a handful of distinct markings (K) and a handful of subject profiles. So we
// pay `decide` per distinct CLASS, not per object.
//
// The load-bearing correctness rule: CLS_ID is the canonical fingerprint of the
// EVALUATOR'S OWN derived outputs — the deriveBoundFqns SET + the validateMarkings
// .valid bit + the RAW (case-sensitive) isProtected inputs + the RAW classification
// string. It is NEVER a re-normalization of raw fields. "same CLS_ID ⇒ byte-identical
// decideVisibility output" is then true BY CONSTRUCTION (no parallel normalization can
// be more aggressive than the evaluator and merge two classes the evaluator splits).
// The cached value IS a literal decideVisibility return — never a re-derivation.

import {
  decideVisibility,
  deriveBoundFqns,
  validateMarkings,
  type PolicyBundle,
  type SubjectClaims,
  type VisibilityResult,
} from "../../../../packages/abac-pdp/src/index.ts";
import type { CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";
// SINGLE SOURCE OF TRUTH for the bundle-free shape half of the three-case marking
// taxonomy (avoids FINDINGS F-LATTICE-1 drift). The same primitives the tak-gateway
// imports — so the COP PDP and the gateway can never diverge on what "broken-shape"
// or "unmarked" means. classifyMarking() below layers the bundle-driven
// validateMarkings.valid contradiction check on top of these.
import { isWellShaped, isUnmarked } from "../../../../packages/schema/ts/marking-shape.ts";

/** The PDP annotation we stamp onto a visible envelope's properties so the
 *  renderer can draw the badge and the inspector can show the reasoning. */
export interface PdpProps {
  /** 🔒 TDF PROTECTED — visible object with an encrypted payload. */
  tdf_protected: boolean;
  /** The PDP verdict (permit, since hidden objects never reach render). */
  pdp_permit: boolean;
  /** Human reason for the (i) inspector. */
  pdp_reason: string;
  /** The bound attribute FQNs the decision was made over. */
  bound_fqns: string[];
  /** Marking-validation warnings (non-blocking; errors fail-closed before render). */
  pdp_warnings: string[];
}

/** Clone an envelope with the PDP decision stamped into its properties (does not
 *  mutate the registry copy, so a re-filter re-evaluates from clean inputs). */
export function annotateForRender(
  env: CanonicalEnvelope,
  res: VisibilityResult,
): CanonicalEnvelope {
  const props: PdpProps = {
    tdf_protected: res.presentation.protectedBadge,
    pdp_permit: res.decision.permit,
    pdp_reason: res.decision.reason,
    bound_fqns: res.boundFqns,
    pdp_warnings: res.validation.violations
      .filter((v) => v.severity === "warning")
      .map((v) => v.message),
  };
  return { ...env, properties: { ...env.properties, ...props } };
}

// (applyGate / GateEngine were REMOVED in Increment 3 — the engine-write bypass closure.
// They were the old per-publish paint helper; the host now paints via cop.ts's paintRow
// (the sole engine writer). Removing them deletes a latent engine-write helper a future
// capability could re-import to bypass the projection. annotateForRender (above) stays —
// it is the shared "stamp the PDP decision onto the rendered env" primitive.)

export interface CopPdp {
  /** Switch the subject identity — the logged-in user's JWT claims. Re-evaluations
   *  use the new entitlements (drives the whole-picture re-filter on persona switch). */
  setIdentity(claims: SubjectClaims): void;
  /** Drop the ENTIRE recorded picture (registry + per-object class bindings), returning
   *  the PDP to a just-constructed empty state under the SAME subject/bundle. The interned
   *  class table + subject-decision memo are kept (pure caches keyed by fingerprint/profile
   *  — harmless across a wipe, and a re-record re-binds each id). For a LIVE theater switch:
   *  the host wipes the engine, then clears the PDP so a subsequent re-filter/reproject can
   *  NEVER resurrect the old theater (registryValues()/visibleEnvelopes() go empty at once). */
  clear(): void;
  /** Record an envelope into the full-picture registry (call on every publish). */
  record(env: CanonicalEnvelope): void;
  /** Decide visibility for an envelope under the current operator. */
  evaluate(env: CanonicalEnvelope): VisibilityResult;
  /** Like `evaluate(env).presentation.visible` but MEMOIZED per claims-epoch — the data seam
   *  decides each envelope ONCE per tick and shares the bit across ALL subscribers, turning the
   *  O(S×E) per-tick cost into O(E) + O(S) lookups. Validity is keyed by envelope id + SECURITY
   *  IDENTITY (not id-existence): the same fan-out (same env object) hits; a re-published
   *  envelope with a new `security` re-decides; the whole cache is cleared on setIdentity. The
   *  returned bit is byte-identical to `evaluate(env).presentation.visible` within the epoch. */
  isVisibleCached(env: CanonicalEnvelope): boolean;
  /** Every envelope seen (the unfiltered picture) — for the whole-picture re-filter. */
  registryValues(): CanonicalEnvelope[];
  /** The currently VISIBLE envelopes — for a clearance-safe banner high-water. */
  visibleEnvelopes(): CanonicalEnvelope[];
  /** The active policy bundle (e.g. to display the namespace in the inspector). */
  bundle(): PolicyBundle;
  /** The claims currently driving decisions — the SINGLE SOURCE OF TRUTH shared by
   *  the render filter, the data-seam filter, and the command decide() (ADR-0008),
   *  so the enforcement points can never gate on divergent claims. */
  currentClaims(): SubjectClaims;
}

/** The reserved CLS_ID for a marking we DETECT but cannot fully parse/reconcile
 *  (Case 3 — BROKEN-present). It indexes no class array, matches no histogram
 *  bucket, and is hidden + loudly logged in every seam method. NEVER a positive id,
 *  so a broken marking can never collide with a real class (id 0 is a real class). */
const DENY_SENTINEL = -1;

const lc = (s: string): string => String(s).toLowerCase();

/** The evaluator's EXACT user-value projection for one attribute — mirrors
 *  evaluate.ts:userValuesFor byte-for-byte: map(lc), NO sort, NO trim. Preserves
 *  userValues[0] (the hierarchy slot evaluateGroup reads). */
function userValuesFor(claims: SubjectClaims, attr: string): string[] {
  const claim = claims[attr];
  if (Array.isArray(claim)) return claim.map(lc);
  if (claim != null && claim !== "") return [lc(String(claim))];
  return [];
}

type MarkingCase = "unmarked" | "clean" | "broken";

/** What a fully built CopPdp engine needs from one recorded class. */
interface ClassRecord {
  /** The frozen raw SecurityView actually passed to decideVisibility at intern time,
   *  so re-evaluation for a NEW subject uses byte-identical inputs (never a
   *  re-derivation). */
  readonly view: CanonicalEnvelope["security"];
}

// isWellShaped / isUnmarked are now imported from @tak-tdf/schema/marking-shape
// (the SINGLE SOURCE OF TRUTH the gateway shares). classifyMarking below uses the
// PDP default (strictEmptyClassification=false), preserving the exact byte-identical
// behavior the lockstep oracle depends on for typed envelopes.

// ── Exported PURE primitives (the recompute engine's correctness core) ──
// These are exported ONLY so the fingerprint-decide-fuzz gate can assert
// "CLS_ID-equal ⟺ decideVisibility-equal" and "profileKey-equal ⟺ decide-equal"
// against the EXACT functions the engine uses. They do NOT widen the CopPdp
// interface (lockstep/glomar/reliability-check still guard the engine unchanged).

/** The reserved CLS_ID for a BROKEN-present marking (Case 3). Exported for gates. */
export const CLS_DENY_SENTINEL = DENY_SENTINEL;

/** Decide the three-case marking taxonomy (the fail-closed chokepoint), bundle-driven. */
export function classifyMarking(
  security: CanonicalEnvelope["security"],
  bundle: PolicyBundle,
): MarkingCase {
  if (!isWellShaped(security)) return "broken";
  if (isUnmarked(security)) return "unmarked";
  // CLEAN vs BROKEN-present hinges on validateMarkings.valid (the only validation bit
  // decideVisibility consumes). valid===false (e.g. NOFORN + foreign REL) is a
  // present-but-unreconcilable marking → Case 3 (still hidden, but now SURFACED).
  try {
    return validateMarkings(security, bundle).valid ? "clean" : "broken";
  } catch {
    return "broken";
  }
}

/** The synthetic default-class view an UNMARKED object (Case 1) is DECIDED as. */
export function defaultClassView(unmarkedHandledAs: string): CanonicalEnvelope["security"] {
  return { classification: unmarkedHandledAs } as unknown as CanonicalEnvelope["security"];
}

/** The CLS_ID fingerprint of the EVALUATOR'S OWN derived outputs for a CLEAN (or
 *  default-class) view: the deriveBoundFqns SET + the validateMarkings.valid bit +
 *  the RAW (case-sensitive) isProtected inputs + the RAW classification string.
 *  Same fingerprint ⟺ byte-identical decideVisibility output, BY CONSTRUCTION. */
export function classIdFingerprint(
  security: CanonicalEnvelope["security"],
  bundle: PolicyBundle,
): string {
  const boundFqns = deriveBoundFqns(security, bundle);
  const validation = validateMarkings(security, bundle);
  const s = security as unknown as Record<string, unknown>;
  return JSON.stringify([
    "v1",
    // bound-FQN SET — order/dup invariant per evaluateAbac. Sorted-unique.
    [...new Set(boundFqns)].sort(),
    // the only validation bit decideVisibility reads (decide.ts:50).
    validation.valid,
    // RAW isProtected inputs (NO normalization — isProtected uses === / !==).
    s.payload_mode === undefined ? null : s.payload_mode,
    s.leakage_profile === undefined ? null : s.leakage_profile,
    // RAW classification string — recomputeBanner reads CLASS_ORDER[s.classification].
    s.classification === undefined ? null : s.classification,
  ]);
}

/** The full CLS_ID-equivalence KEY for an object: its three-case outcome folded in.
 *  BROKEN → the deny sentinel marker; UNMARKED → the fingerprint of the default-class
 *  view; CLEAN → the fingerprint of the raw view. Two objects share this key IFF the
 *  engine assigns them the same CLS_ID — the exact ⟺ the fuzz gate asserts against
 *  decideVisibility. */
export function classKeyOf(
  security: CanonicalEnvelope["security"],
  bundle: PolicyBundle,
  unmarkedHandledAs = "UNCLASSIFIED",
): string {
  const kase = classifyMarking(security, bundle);
  if (kase === "broken") return `DENY_SENTINEL`;
  const view = kase === "unmarked" ? defaultClassView(unmarkedHandledAs) : security;
  return `CLS:${classIdFingerprint(view, bundle)}`;
}

/** The decideVisibility result the engine WILL serve for an object, honoring the
 *  three-case taxonomy (BROKEN → deny; UNMARKED → decided as the default class;
 *  CLEAN → decided as-is). This is the byte-identical oracle the fuzz gate compares
 *  the engine's seam output against. */
export function decideForClass(
  security: CanonicalEnvelope["security"],
  bundle: PolicyBundle,
  claims: SubjectClaims,
  unmarkedHandledAs = "UNCLASSIFIED",
): VisibilityResult {
  const kase = classifyMarking(security, bundle);
  if (kase === "broken") {
    return {
      decision: { permit: false, reason: "invalid markings (fail-closed): broken-present marking (DENY_SENTINEL)", verdicts: [] },
      presentation: { visible: false, protectedBadge: false },
      boundFqns: [],
      validation: { valid: false, violations: [], categorized: {} },
    };
  }
  const view = kase === "unmarked" ? defaultClassView(unmarkedHandledAs) : security;
  return decideVisibility(view, bundle, claims);
}

/** The SUBJECT profileKey — the evaluator's EXACT userValuesFor projection over
 *  bundle.attributes, UNSORTED (preserving userValues[0]). profileKey-equal ⟺
 *  decideVisibility-equal for the same class, BY CONSTRUCTION. */
export function subjectProfileKey(claims: SubjectClaims, bundle: PolicyBundle): string {
  return JSON.stringify(bundle.attributes.map((a) => [lc(a.name), userValuesFor(claims, a.name)]));
}

/** Build the COP PDP from the LIVE policy bundle (fetched from TDFLite's
 *  /api/policy-attrs) and the logged-in identity's JWT claims as the ABAC subject.
 *
 *  `unmarkedHandledAs` (default "UNCLASSIFIED") is the deployment policy knob for
 *  Case 1 (UNMARKED): an explicit configurable default level, never a silent permit.
 *  A stricter site may set it to a higher floor. */
export function createCopPdp(opts: {
  bundle: PolicyBundle;
  claims: SubjectClaims;
  unmarkedHandledAs?: string;
}): CopPdp {
  const bundle = opts.bundle;
  let claims: SubjectClaims = opts.claims;
  const unmarkedHandledAs = opts.unmarkedHandledAs ?? "UNCLASSIFIED";

  const registry = new Map<string, CanonicalEnvelope>();

  // ── interning: fingerprint → dense CLS_ID, and per-class cached inputs ──
  const fpToId = new Map<string, number>();
  const classes: ClassRecord[] = [];
  /** objectId → CLS_ID (or DENY_SENTINEL). Re-fingerprinted on EVERY content change. */
  const objToCls = new Map<string, number>();
  /** objectId → the cheap RAW-BYTES pre-key of the security last fingerprinted for
   *  that id (AOA open-Q1's sanctioned fast path). It is SPLIT-ONLY: different raw
   *  bytes never share a pre-key, so it can never MERGE two classes (the full
   *  evaluator-output fingerprint still runs on any new/changed pre-key). It is
   *  content-keyed (not security-REF identity), so it CATCHES in-place mutation of
   *  the same security object — the AOA A#4 hazard — while letting an exact-byte
   *  re-publish (the fan-out firehose) skip the derive+validate. */
  const objToRawKey = new Map<string, string>();
  /** A cheap, content-faithful serialization of the raw security fields the
   *  fingerprint depends on. Split-only: any byte difference yields a different key.
   *  NO normalization (so it can never merge what the evaluator distinguishes). */
  const rawKeyOf = (security: unknown): string => {
    try {
      const s = security as Record<string, unknown>;
      return JSON.stringify([
        s.classification, s.releasability, s.caveats, s.mission,
        s.payload_mode, s.leakage_profile,
      ]);
    } catch {
      // Non-serializable (cycles, etc.) → unique key so it always re-fingerprints.
      return `\u0000nonserializable\u0000${Math.random()}`;
    }
  };

  // ── per-subject decision memo (the per-(CLS_ID, subject) table) ──
  // We key columns by profileKey and keep them across subject switches (switch-back
  // is free). The CURRENT subject's column is the hot read.
  interface SubjectColumn {
    readonly profileKey: string;
    readonly claims: SubjectClaims;
    /** CLS_ID → cached VisibilityResult for THIS subject (lazily filled). */
    readonly results: (VisibilityResult | undefined)[];
  }
  const columns = new Map<string, SubjectColumn>();
  let current: SubjectColumn = columnFor(claims);

  // ── incremental visible-set cache ──
  // visibleEnvelopes() materializes an O(visible) array of cleared envelopes. That
  // array changes ONLY when (a) the subject column changes (setIdentity) or (b) a
  // record() actually changes an object's CLS_ID / adds a new id. A pure dot
  // re-publish of an ALREADY-SEEN marking changes nothing → the cached array is
  // returned in O(1) (the AOA "movement firehose is O(1)" guarantee). The cache is
  // tagged with (column, recordVersion); any mismatch rebuilds from the memo (O(N)
  // cheap O(1) bit lookups — zero decide calls). It stays byte-identical to a fresh
  // full rebuild because it IS one, recomputed exactly when an input changed.
  let recordVersion = 0;
  let cachedVisible: CanonicalEnvelope[] | null = null;
  let cachedVisibleCol: SubjectColumn | null = null;
  let cachedVisibleVersion = -1;

  function columnFor(c: SubjectClaims): SubjectColumn {
    // The evaluator's EXACT userValuesFor projection over bundle.attributes,
    // UNSORTED — the single source of truth shared with the fuzz gate.
    const profileKey = subjectProfileKey(c, bundle);
    const existing = columns.get(profileKey);
    if (existing) return existing;
    const col: SubjectColumn = { profileKey, claims: c, results: [] };
    columns.set(profileKey, col);
    return col;
  }

  // LOUD-ONCE BROKEN-MARKING LOG. A BROKEN marking is an upstream feed bug an admin must fix,
  // and the error log is the alarm — but the SAME broken object republishing at feed cadence
  // must not storm the console (observed on prod: one malformed object re-logged ~1 Hz, 678+
  // errors/session). First sighting per object id logs at error; repeats are SILENT. The DROP
  // itself is unconditional and undeduped — only the LOG dedupes; fail-closed is untouched.
  // Dedup tracking is bounded: past the cap (a catastrophically broken feed), logging reverts
  // to per-sighting — deliberately loud again, because at that scale the storm IS the signal.
  const BROKEN_LOG_CAP = 500;
  const brokenLogged = new Set<string>();
  function logBrokenOnce(id: string, message: string): void {
    if (brokenLogged.has(id)) return;
    if (brokenLogged.size < BROKEN_LOG_CAP) brokenLogged.add(id);
    console.error(message);
  }

  /** Canonicalize a recorded envelope to its CLS_ID, minting/interning as needed.
   *  Returns DENY_SENTINEL for a BROKEN marking (Case 3) — and logs loudly (once per
   *  object — see logBrokenOnce). Never throws; a thrown canonicalization is also
   *  fail-closed to DENY_SENTINEL. */
  function canonicalize(env: CanonicalEnvelope): number {
    let kase: MarkingCase;
    let view: CanonicalEnvelope["security"];
    try {
      kase = classifyMarking(env.security, bundle);
      view = kase === "unmarked" ? defaultClassView(unmarkedHandledAs) : env.security;
    } catch (e) {
      // A throw in the real functions (unexpected shape) is fail-closed to Case 3.
      logBrokenOnce(
        env.id,
        `[CopPdp] BROKEN marking (fail-closed, hidden): object ${env.id} — canonicalization threw: ${String(e)}`,
      );
      return DENY_SENTINEL;
    }
    if (kase === "broken") {
      // Case 3 — present-but-unreconcilable. Hidden in every seam + loud log so an
      // admin can fix the feed. NEVER coerced to a default/positive id.
      logBrokenOnce(
        env.id,
        `[CopPdp] BROKEN marking (fail-closed, hidden): object ${env.id} — ` +
          `marking present but malformed-shape or validateMarkings.valid===false; assigned DENY_SENTINEL.`,
      );
      return DENY_SENTINEL;
    }
    if (kase === "unmarked") {
      // Case 1 — debug-level; an explicit policy knob, not a silent fallback.
      // (Logged at debug to avoid console spam over a large unmarked feed.)
      console.debug(`[CopPdp] UNMARKED object ${env.id} handled as default class "${unmarkedHandledAs}".`);
    }
    let fp: string;
    try {
      fp = classIdFingerprint(view, bundle);
    } catch (e) {
      logBrokenOnce(
        env.id,
        `[CopPdp] BROKEN marking (fail-closed, hidden): object ${env.id} — fingerprint threw: ${String(e)}`,
      );
      return DENY_SENTINEL;
    }
    let id = fpToId.get(fp);
    if (id === undefined) {
      id = classes.length;
      classes.push({ view: Object.freeze({ ...(view as object) }) as CanonicalEnvelope["security"] });
      fpToId.set(fp, id);
    }
    return id;
  }

  /** The cached decideVisibility result for (clsId, current subject). The stored
   *  entry IS a literal decideVisibility output against classes[clsId].view (the
   *  frozen raw inputs) and the column's claims — never a re-derived approximation. */
  function resultFor(col: SubjectColumn, clsId: number): VisibilityResult {
    const cached = col.results[clsId];
    if (cached !== undefined) return cached;
    const res = decideVisibility(classes[clsId].view, bundle, col.claims);
    col.results[clsId] = res;
    return res;
  }

  /** The CLS_ID for an envelope's CURRENT security — a PURE read-path resolver.
   *  Honors the rawKey fast path (a record()'d env with byte-equal security reuses
   *  its interned class) but NEVER mutates shared state: it does NOT write objToCls /
   *  objToRawKey / registry, and does NOT bump recordVersion. This is the security-
   *  critical separation: the only writer of (registry, objToCls) is record(), in
   *  lockstep. A read-path probe of a NON-CANONICAL env (e.g. a retained, more-
   *  permissive selection snapshot of an id that record() later upgraded) computes a
   *  correct local CLS_ID for the probed env WITHOUT re-pointing objToCls — so it can
   *  never desync the maps and make visibleEnvelopes() serve a registry envelope under
   *  a stale class (the SECRET-leak the prior write side-effect caused). Interning a
   *  NEW class id (a fingerprint never seen by record()) is the one allowed effect —
   *  it only appends to fpToId/classes and is split-only, never a merge/leak, and is
   *  shared with record() so the dense ids stay consistent. */
  function clsIdForCurrent(env: CanonicalEnvelope): number {
    const rawKey = rawKeyOf(env.security);
    // Fast path: an id record()'d with byte-equal security has an authoritative class.
    if (objToCls.has(env.id) && objToRawKey.get(env.id) === rawKey) {
      return objToCls.get(env.id)!;
    }
    // The probed env's content differs from (or precedes) what record() interned for
    // this id. Compute the class for THE PROBED ENV's own content, with NO write-back.
    return canonicalize(env);
  }

  /** The DENY result the seam methods return for a BROKEN (DENY_SENTINEL) object —
   *  byte-identical in shape to a real fail-closed-on-invalid decideVisibility. */
  function deniedResult(): VisibilityResult {
    return {
      decision: {
        permit: false,
        reason: "invalid markings (fail-closed): broken-present marking (DENY_SENTINEL)",
        verdicts: [],
      },
      presentation: { visible: false, protectedBadge: false },
      boundFqns: [],
      validation: { valid: false, violations: [], categorized: {} },
    };
  }

  return {
    setIdentity(next: SubjectClaims): void {
      claims = next;
      current = columnFor(next); // reuse the cached column if this profile is known
    },
    clear(): void {
      // Empty the recorded picture + its per-object class bindings. We DON'T clear the
      // interned class table (fpToId/classes) or the subject columns: those are pure,
      // fingerprint/profile-keyed caches — a re-record of a new theater either reuses an
      // existing class or mints a new one, and the decision memo stays byte-correct. We
      // DO invalidate the visible-set cache + bump the version so visibleEnvelopes()
      // rebuilds to empty on the next read (no stale theater can survive).
      registry.clear();
      objToCls.clear();
      objToRawKey.clear();
      cachedVisible = null;
      cachedVisibleCol = null;
      cachedVisibleVersion = -1;
      recordVersion++;
    },
    record(env: CanonicalEnvelope): void {
      const hadId = objToCls.has(env.id);
      const prev = objToCls.get(env.id);
      registry.set(env.id, env);
      // RAW-BYTES fast path (split-only, never merges): if this id's raw security
      // serializes to the SAME bytes we last fingerprinted, the CLS_ID cannot have
      // changed — reuse it and skip the derive+validate. This is content-keyed (NOT
      // security-ref identity), so an in-place mutation of the same object (A#4)
      // changes the bytes → re-fingerprints; an exact-byte re-publish (the fan-out
      // firehose) hits the fast path. Either way we never gate on ref identity.
      const rawKey = rawKeyOf(env.security);
      let next: number;
      if (hadId && objToRawKey.get(env.id) === rawKey) {
        next = prev!; // same content as last time → same class, by construction
      } else {
        // New id, or content changed → run the full evaluator-output fingerprint.
        next = canonicalize(env);
        objToRawKey.set(env.id, rawKey);
      }
      objToCls.set(env.id, next);
      // Bump the visible-set version ONLY when the picture actually changes (new id,
      // or a marking that re-classes the object). A re-publish of a seen marking is a
      // no-op → the cached visible array stays valid → visibleEnvelopes() is O(1).
      if (!hadId || prev !== next) recordVersion++;
    },
    evaluate(env: CanonicalEnvelope): VisibilityResult {
      // evaluate() is called from the host on arbitrary envelopes (incl. ones never
      // record()'d, e.g. a retained selection snapshot, OR a re-published env not yet
      // re-record()'d). Resolve the CLS_ID from the env's CURRENT security via the
      // PURE resolver: it decides the PROBED env on its own content and NEVER writes
      // objToCls/registry, so probing a non-canonical (e.g. more-permissive) snapshot
      // of an id cannot desync the maps or leak a higher-marked registry envelope via
      // visibleEnvelopes(). record() remains the sole writer of (registry, objToCls).
      const clsId = clsIdForCurrent(env);
      if (clsId === DENY_SENTINEL) {
        // LOUD-ONCE per (site, object): the per-publish evaluate of a broken object fires ~1 Hz
        // on the live feed — the alarm rings once per surface, the DROP stays unconditional.
        logBrokenOnce(`evaluate|${env.id}`, `[CopPdp] BROKEN marking hidden in evaluate(): object ${env.id} (DENY_SENTINEL).`);
        return deniedResult();
      }
      return resultFor(current, clsId);
    },
    isVisibleCached(env: CanonicalEnvelope): boolean {
      const clsId = clsIdForCurrent(env);
      if (clsId === DENY_SENTINEL) {
        logBrokenOnce(`isVisibleCached|${env.id}`, `[CopPdp] BROKEN marking hidden in isVisibleCached(): object ${env.id} (DENY_SENTINEL).`);
        return false;
      }
      return resultFor(current, clsId).presentation.visible;
    },
    registryValues(): CanonicalEnvelope[] {
      return [...registry.values()];
    },
    visibleEnvelopes(): CanonicalEnvelope[] {
      // O(1) cache hit when neither the subject nor the registry changed since the
      // last build (the per-publish dot firehose). Otherwise rebuild from the memo:
      // O(N) cheap O(1) lookups, ZERO decide calls. The rebuilt array is byte-
      // identical to a fresh full rescan because each bit IS decideVisibility's output.
      if (cachedVisible !== null && cachedVisibleCol === current && cachedVisibleVersion === recordVersion) {
        return cachedVisible;
      }
      const out: CanonicalEnvelope[] = [];
      for (const env of registry.values()) {
        const clsId = objToCls.get(env.id);
        if (clsId === undefined || clsId === DENY_SENTINEL) {
          if (clsId === DENY_SENTINEL) {
            logBrokenOnce(`visibleEnvelopes|${env.id}`, `[CopPdp] BROKEN marking hidden in visibleEnvelopes(): object ${env.id} (DENY_SENTINEL).`);
          }
          continue; // unrecorded-but-in-registry can't happen (record sets both); deny is hidden
        }
        if (resultFor(current, clsId).presentation.visible) out.push(env);
      }
      cachedVisible = out;
      cachedVisibleCol = current;
      cachedVisibleVersion = recordVersion;
      return out;
    },
    bundle(): PolicyBundle {
      return bundle;
    },
    currentClaims(): SubjectClaims {
      return claims;
    },
  };
}
